/**
 * SalesInput.js
 * 版本：V1.02 | 更新日期：2025-07-19
 * 功能：銷售數量輸入組件，提供銷售資料輸入和儲存功能
 * 關連檔案：App.js, DayDetailModal.js
 * 
 * 更新內容：
 * V1.02 (2025-07-19) - 修正表單文字顏色問題
 * - 為所有下拉選單和輸入欄位添加 text-gray-700 bg-white 樣式
 * - 解決在某些背景下文字顯示為白色而看不清楚的問題
 * - 確保表單元素在所有背景下都有良好的可讀性
 * 
 * V1.01 (2025-07-16) - 初始版本
 * - 基本銷售數量輸入功能
 * - 多商品銷售記錄
 * - 資料儲存和驗證
 */

import React, { useState, useEffect } from 'react';
import { doc, updateDoc, serverTimestamp, collection, getDocs } from 'firebase/firestore';

const SalesInput = ({ booking, db, appId, onSaveSuccess, currentUser }) => {
    const [products, setProducts] = useState([]);
    const [salesDetails, setSalesDetails] = useState(
        booking.salesDetails && Array.isArray(booking.salesDetails) && booking.salesDetails.length > 0
            ? booking.salesDetails.map(item => ({ ...item }))
            : [{ productId: '', quantity: '' }]
    );
    const [isSaving, setIsSaving] = useState(false);
    const [loadingProducts, setLoadingProducts] = useState(true);

    useEffect(() => {
        const fetchProducts = async () => {
            setLoadingProducts(true);
            try {
                const productsCol = collection(db, `artifacts/${appId}/public/data/products`);
                const snapshot = await getDocs(productsCol);
                setProducts(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            } catch (e) {
                setProducts([]);
            } finally {
                setLoadingProducts(false);
            }
        };
        if (db && appId) fetchProducts();
    }, [db, appId]);

    const handleChange = (idx, field, value) => {
        setSalesDetails(details => details.map((item, i) => i === idx ? { ...item, [field]: value } : item));
    };
    const handleAddRow = () => {
        setSalesDetails(details => [...details, { productId: '', quantity: '' }]);
    };
    const handleRemoveRow = (idx) => {
        setSalesDetails(details => details.length === 1 ? details : details.filter((_, i) => i !== idx));
    };
    const handleSave = async () => {
        if (!currentUser || currentUser.id !== booking.vendorId) {
            alert('您沒有權限修改此筆銷售資料！');
            return;
        }
        // 檢查必填
        for (const item of salesDetails) {
            if (!item.productId || !item.quantity || isNaN(Number(item.quantity)) || Number(item.quantity) < 0) {
                alert('請正確填寫所有商品與數量！');
                return;
            }
        }
        setIsSaving(true);
        try {
            const bookingRef = doc(db, `artifacts/${appId}/public/data/bookings`, booking.id);
            await updateDoc(bookingRef, {
                salesDetails: salesDetails.map(item => ({ productId: item.productId, quantity: Number(item.quantity) })),
                salesUpdatedAt: serverTimestamp(),
                salesUpdatedBy: currentUser.id
            });
            if (onSaveSuccess) onSaveSuccess();
        } catch (error) {
            console.error('儲存銷售資料失敗:', error);
            alert('儲存失敗，請稍後再試！');
        } finally {
            setIsSaving(false);
        }
    };
    return (
        <div className="mt-2 flex flex-col gap-2">
            {salesDetails.map((item, idx) => (
                <div className="flex gap-2 items-center" key={idx}>
                    <select
                        value={item.productId}
                        onChange={e => handleChange(idx, 'productId', e.target.value)}
                        className="flex-1 p-1 border rounded text-gray-700 bg-white"
                        disabled={loadingProducts}
                    >
                        <option value="">選擇商品</option>
                        {products.map(p => (
                            <option key={p.id} value={p.id}>{p.name}</option>
                        ))}
                    </select>
                    <input
                        type="number"
                        value={item.quantity}
                        onChange={e => handleChange(idx, 'quantity', e.target.value)}
                        placeholder="數量"
                        className="w-20 p-1 border rounded text-gray-700 bg-white"
                        min="0"
                    />
                    {salesDetails.length > 1 && (
                        <button type="button" onClick={() => handleRemoveRow(idx)} className="text-xs bg-red-400 text-white px-2 rounded hover:bg-red-600">刪除</button>
                    )}
                </div>
            ))}
            <div className="flex gap-2 mt-1">
                <button type="button" onClick={handleAddRow} className="text-xs bg-green-500 text-white px-3 rounded hover:bg-green-600">＋新增商品</button>
                <button
                    onClick={handleSave}
                    disabled={isSaving || loadingProducts}
                    className="bg-blue-500 text-white text-xs px-3 rounded hover:bg-blue-600 disabled:bg-gray-400"
                >
                    {isSaving ? '儲存中...' : '儲存'}
                </button>
            </div>
        </div>
    );
};

export default SalesInput; 